import React, { useState, useEffect } from 'react';
import { Badge, Tooltip, useComputedColorScheme } from '@mantine/core';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { CollapseUnmount } from '~/components/common/collapse/CollapseUnmount';
import { useUpdatePageAliases } from '~/hooks/mutations/operations/use-update-page-aliases';

export default function OperationTabs({
  isForDisplayingOpensPageAlias,
  operation,
  testContentId,
  readonly,
  showTabLabel = false,
}: {
  // isForOpens: whether this tab is for displaying the page that got opened
  isForDisplayingOpensPageAlias: boolean;
  operation: SelectedOperation;
  testContentId: string;
  readonly?: boolean;
  showTabLabel?: boolean;
}) {
  const colorScheme = useComputedColorScheme();

  const [editingPageAlias, setEditingPageAlias] = useState(false);
  const [pageAliasValue, setPageAliasValue] = useState(operation.pageAlias);

  const [editingOpensPageAlias, setEditingOpensPageAlias] = useState(false);
  const [opensPageAliasValue, setOpensPageAliasValue] = useState(operation.opensPageAlias);

  // Ensure state updates if the operation prop changes
  useEffect(() => {
    setPageAliasValue(operation.pageAlias);
  }, [operation.pageAlias]);

  useEffect(() => {
    setOpensPageAliasValue(operation.opensPageAlias);
  }, [operation.opensPageAlias]);

  const { mutate: updatePageAliases } = useUpdatePageAliases({ testContentId });

  if (isForDisplayingOpensPageAlias) {
    if (!operation.opensPageAlias) {
      return null;
    }

    return (
      <div className="mb-4 ml-7 flex flex-row items-center gap-2 pb-1">
        {!!operation.opensPageAlias && (
          <div className="text-xs font-medium text-blue-800 dark:text-blue-400">opens</div>
        )}
        {!!operation.opensPageAlias && (
          <Tooltip label="Click to edit">
            <Badge
              size="sm"
              variant="dot"
              color={colorScheme === 'dark' ? 'white' : 'gray'}
              className="font-mono normal-case opacity-70"
              onClick={() => !readonly && setEditingOpensPageAlias(true)}
              style={{ cursor: readonly ? 'default' : 'pointer' }}
            >
              {editingOpensPageAlias ?
                <input
                  type="text"
                  value={opensPageAliasValue ?? ''}
                  onChange={(e) => setOpensPageAliasValue(e.target.value)}
                  onBlur={() => {
                    setEditingOpensPageAlias(false);
                    if (opensPageAliasValue !== operation.opensPageAlias) {
                      updatePageAliases({
                        operationId: operation.id,
                        pageAlias: operation.pageAlias,
                        opensPageAlias: opensPageAliasValue,
                      });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setOpensPageAliasValue(operation.opensPageAlias);
                      setEditingOpensPageAlias(false);
                    }
                  }}
                  className="w-20 bg-transparent px-1 font-mono"
                  autoFocus
                />
              : `Tab ${operation.opensPageAlias}`}
            </Badge>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-2">
      <CollapseUnmount
        in={showTabLabel}
        transitionDuration={300}
      >
        <div className="ml-1 flex items-center gap-1.5">
          <Tooltip label="Click to edit">
            <Badge
              size="sm"
              variant="dot"
              color={colorScheme === 'dark' ? 'white' : 'gray'}
              className="font-mono normal-case opacity-70"
              onClick={() => !readonly && setEditingPageAlias(true)}
              style={{ cursor: readonly ? 'default' : 'pointer' }}
            >
              {editingPageAlias ?
                <input
                  type="text"
                  value={pageAliasValue}
                  onChange={(e) => setPageAliasValue(e.target.value)}
                  onBlur={() => {
                    setEditingPageAlias(false);
                    if (pageAliasValue !== operation.pageAlias) {
                      updatePageAliases({
                        operationId: operation.id,
                        pageAlias: pageAliasValue,
                        opensPageAlias: operation.opensPageAlias,
                      });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setPageAliasValue(operation.pageAlias);
                      setEditingPageAlias(false);
                    }
                  }}
                  className="w-20 bg-transparent px-1 font-mono"
                  autoFocus
                />
              : `Tab: ${operation.pageAlias}`}
            </Badge>
          </Tooltip>
        </div>
      </CollapseUnmount>
    </div>
  );
}
